/* eslint-disable one-var */
/* eslint-disable no-console */
/* eslint-disable func-names */
import getCallCenterStatus from '../00-utilities/call-center-status.js';

/**
 * @fileOverview Hide call blocks depending on call center status.
 * @author Selectra Front End Team
 * @version 1.0.0
 * @requires Polyfill:Fetch
 * @requires Polyfill:Element.prototype.classList
 * @requires Polyfill:NodeList.prototype.forEach
 * @requires '../00-utilities/call-center-status.js'
 * @requires '../00-data/callcenter-ids.json'
 */


/**
 * @async
 * @function displayDynamicCallBlocks
 * @description  Retrieves call center status from Dialga API to activate
 *     the proper set of open/closed dynamic call blocks based on the current
 *     availability of the call centers.
 * @param {object} [opt] - Options object
 * @param {string} [opt.openSelectors=.call-block:not(.call-block--closed)]
 *     A string of CSS selectors, used to select the 'Open' call-center blocks
 *     in the DOM.
 * @param {string} [opt.closedSelectors=.call-block.call-block--closed]
 *     A string of CSS selectors, used to locate the 'Closed' call-center blocks
 *     in the DOM.
 * @param {string} [opt.activeClassName=call-block--active]
 *     The CSS class name to be added to the blocks we want to show.
 * @param {string} [opt.dataPath=/js/data/callcenter-ids.json]
 *     The path to the call center data file.
 * @returns {Promise} The ccStatus object is returned if everything went well,
 *     if some error occurred, then an object describing the error is thrown.
 */
export default async function displayDynamicCallBlocks( opt = {}) {

  const

    // Set default options.
    // Coded this way so we can pass other class names when calling this
    // component if necessary.
    // These default values should not be changed otherwise.
    defaultOptions = {
      openSelectors: '[data-callcenter].call-block--open ',
      closedSelectors: '[data-callcenter].call-block--closed',
      activeClassName: 'call-block--active',
      dataPath: '/js/data/callcenter-ids.json'
    },
    options = { ...defaultOptions, ...opt },

    // Get references of dynamic call-blocks on the page
    blocks = [
      {
        type: 'open',
        elements: document.querySelectorAll( options.openSelectors ),
        ccIDs: [],
      },
      {
        type: 'closed',
        elements: document.querySelectorAll( options.closedSelectors ),
        ccIDs: [],
      },
    ],

    // Rejected promise throw object
    error = {
      id: null,
      status: null,
      url: null,
    };

  let
    // Call center status object
    ccStatus = {};

  // If no dynamic call-blocks where found... kthxbai!
  if ( !( blocks[ 0 ].elements.length + blocks[ 1 ].elements.length )) {
    return null;
  }

  // Fetch the call-centers data object, it's a map of call-center string IDs
  // to the numerical IDs dialga uses.
  return fetch( options.dataPath, { method: 'GET', mode: 'cors' })
    .then( response => {
      const status = response.status;
      if ( status === 200 ) {
        return response.json();
      } else if ( status ) {
        error.id = 'FetchError';
        error.status = status;
        error.url = response.url;
        throw error;
      }
    })
    .then( ccData => {

      const groupsToFetch = [];

      // Populate 'groupsToFetch' and 'ccId' arrays
      blocks.forEach( obj => {
        let totalElements = obj.elements.length;
        for ( let i = 0; i < totalElements; ++i ) {
          const ccId = ccData[ obj.elements[ i ].dataset.callcenter ];
          if ( ccId && !groupsToFetch.includes( ccId )) {
            groupsToFetch.push( ccId );
          }
          obj.ccIDs[ i ] = ccId;
        }
      });

      // Fetch call-centers status
      return getCallCenterStatus({ groupIDs: groupsToFetch })
        .then( status => {
          ccStatus = status;
          return ccStatus;
        })
        .catch( error => {
          console.error( 'Failed fetching call centers status:', error.id );
          console.warn( 'Fall back to display default call-blocks' );
          setDefaultCcStatus();
          throw error;
        })
        .finally( displayCallBlocks );
    })
    .catch( error => {
      if ( error.id === 'FetchError' ) {
        console
          .error( `${ error.status }: Could not fetch ${ error.url }` );
      } else {
        console
          .error( `Network error. Could not fetch ${ error.url }` );
      }
      console.warn( 'Falling back to display default call-blocks' );

      setDefaultCcStatus();
      displayCallBlocks();
      throw error;

    });


  /**
   * @function displayCallBlocks
   * @description Process all call-block elements and add the active modifier if
   *     the call-block type (open/closed) corresponds to the current state of
   *     it's associated call center, remove it from the DOM otherwise.
   */
  function displayCallBlocks() {
    blocks.forEach( block => {
      const totalElements = block.elements.length;
      for ( let i = 0; i < totalElements; ++i ) {
        const
          callBlockCcStatus = ccStatus[ block.ccIDs[ i ] ].state,
          callBlockElement = block.elements[ i ];

        if ( block.type === callBlockCcStatus && callBlockElement ) {
          callBlockElement.classList.add( options.activeClassName );
        } else if ( callBlockElement.parentElement ) {
          callBlockElement.parentElement.removeChild( callBlockElement );
        }
      }
    });
  }


  /**
   * @function setDefaultCcStatus
   * @description This will setup two fake call centers, one with the state
   *     'open' and the other 'closed'.
   *     Then it will assign one of these two to each block depending on the
   *     block's default state.
   */
  function setDefaultCcStatus() {

    // Setup fake call-centers.
    ccStatus[ 0 ] = { state: 'open' };
    ccStatus[ 1 ] = { state: 'closed' };

    // Assign fake call center to each block based on the block data.
    blocks.forEach( block => {
      const totalElements = block.elements.length;
      for ( let i = 0; i < totalElements; ++i ) {
        block.ccIDs[ i ] =
          block.elements[ i ].dataset[ 'defaultState' ] === 'closed' ? 1 : 0;
      }
    });
  }

}
